import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Bot, PhoneIncoming, PhoneOutgoing, Save, ArrowLeft, Mic } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { LANGUAGE_OPTIONS, TIMEZONE_OPTIONS } from '../constants/agentOptions';

// Outbound calling is hidden (not removed) pending Australian regulatory clarification.
const OUTBOUND_ENABLED = false;

const TEMPLATES = [
  { id: 'blank', name: 'Blank', desc: 'Start from scratch', icon: '📝' },
  { id: 'receptionist', name: 'Receptionist', desc: 'Answer calls & take messages', icon: '👩‍💼' },
  { id: 'booking', name: 'Booking Agent', desc: 'Handle reservations', icon: '📅' },
  { id: 'support', name: 'Support Agent', desc: 'Answer common questions', icon: '🎧' },
  { id: 'outbound_sales', name: 'Outbound Sales', desc: 'Make outgoing calls', icon: '📞' },
];

export default function CreateAgentPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canTargetOthers = user?.role === 'partner' || user?.role === 'admin';
  const [voices, setVoices] = useState([]);
  const [dids, setDids] = useState([]);
  const [didsError, setDidsError] = useState(false);
  const [potentialOwners, setPotentialOwners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    type: 'inbound',
    template: 'blank',
    language: 'en-US',
    voice: '',
    timezone: 'Australia/Sydney',
    phone_number: '',
    did_number: '',
    did_id: '',
    welcome_message_enabled: true,
    welcome_message_text: '',
    welcome_message_delay: 1,
    voicemail_detection: true,
    voicemail_timeout: 10,
    voicemail_message: 'Please leave a message after the tone.',
    recording_enabled: true,
    system_prompt: '',
    // Действия после звонка настраиваются на вкладке Actions уже созданного агента.
    post_call_actions: {},
    target_user_id: '',
  });

  useEffect(() => {
    api.get('/agents/meta/voices')
      .then(res => {
        const list = res.data.voices || [];
        setVoices(list);
        // Sync the controlled <select> to a real voice id. Otherwise form.voice
        // keeps its initial value, which isn't among the fetched options, and an
        // invalid/stale voice gets submitted to Telnyx (error 10015).
        if (list.length) {
          setForm(f => (list.some(v => v.id === f.voice) ? f : { ...f, voice: list[0].id }));
        }
      })
      .catch(() => {});
    api.get('/agents/meta/dids')
      .then(res => setDids(res.data.dids || []))
      .catch(() => setDidsError(true));
    if (canTargetOthers) {
      api.get('/partner/users')
        .then(res => setPotentialOwners(res.data.users || []))
        .catch(() => {});
    }
  }, [canTargetOthers]);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const applyTemplate = (templateId) => {
    update('template', templateId);
    const templates = {
      receptionist: {
        system_prompt: 'You are a professional receptionist. Greet callers warmly, ask for their name and purpose of call, and offer to take a message or transfer them.',
        welcome_message_text: 'Thank you for calling! This is your virtual receptionist. How may I direct your call today?'
      },
      booking: {
        system_prompt: 'You are a booking assistant. Help callers make, modify or cancel reservations. Collect: name, date, time, party size, contact number. Confirm all details before ending.',
        welcome_message_text: 'Hello! Thank you for calling. I can help you with bookings and reservations. What date were you looking at?'
      },
      support: {
        system_prompt: 'You are a customer support agent. Answer common questions, troubleshoot issues, and collect customer details for escalation if needed.',
        welcome_message_text: "Hi there! I'm your support assistant. How can I help you today?"
      },
    };
    if (templates[templateId]) {
      setForm(f => ({ ...f, template: templateId, ...templates[templateId] }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Agent name is required');
    if (form.type === 'inbound' && !form.did_id) return toast.error('Please select a phone number');

    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.target_user_id) delete payload.target_user_id;
      const res = await api.post('/agents', payload);
      const phoneLinkFailed = res.data.phone_link?.attempted && !res.data.phone_link.success;
      if (!phoneLinkFailed) {
        toast.success('Agent created successfully!');
      }
      if (!res.data.telnyx_synced) {
        toast('Note: Telnyx sync failed - check your API key', { icon: '⚠️' });
      }
      if (phoneLinkFailed) {
        toast(`Agent created, but phone linking failed (${res.data.phone_link.step || 'unknown'}). You can retry from the agent page.`, { icon: '⚠️' });
      }
      navigate(`/agents/${res.data.agent.id}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create agent');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <button className="btn btn-ghost" onClick={() => navigate('/dashboard')}>
          <ArrowLeft size={16} /> Back
        </button>
        <h1>Create New Agent</h1>
      </div>

      <form onSubmit={handleSubmit} className="form-container">
        {canTargetOthers && (
          <div className="form-section">
            <h3>Owner</h3>
            <div className="form-group">
              <label>Create agent for</label>
              <select className="input" value={form.target_user_id}
                onChange={e => update('target_user_id', e.target.value)}>
                <option value="">— myself ({user?.email}) —</option>
                {potentialOwners.map(o => (
                  <option key={o.id} value={o.id}>{o.email} ({o.full_name})</option>
                ))}
              </select>
              <small>Owner is persisted to Telnyx assistant description and used for visibility.</small>
            </div>
          </div>
        )}

        {/* Agent Type */}
        <div className="form-section">
          <h3>Agent Type</h3>
          <div className="type-selector">
            <div
              className={`type-option ${form.type === 'inbound' ? 'type-option--active' : ''}`}
              onClick={() => update('type', 'inbound')}
            >
              <PhoneIncoming size={28} />
              <strong>Inbound</strong>
              <small>Receives incoming calls</small>
            </div>
            {OUTBOUND_ENABLED && (
              <div
                className={`type-option ${form.type === 'outbound' ? 'type-option--active' : ''}`}
                onClick={() => update('type', 'outbound')}
              >
                <PhoneOutgoing size={28} />
                <strong>Outbound</strong>
                <small>Makes outgoing calls</small>
              </div>
            )}
          </div>
          {form.type === 'inbound' && (
            <div className="form-group" style={{ marginTop: 16 }}>
              <label>Phone Number *</label>
              <select className="input" value={form.did_id}
                onChange={e => {
                  const d = dids.find(x => x.id === e.target.value);
                  setForm(f => ({ ...f, did_id: d ? d.id : '', did_number: d ? d.number : '' }));
                }}>
                <option value="">— select an available number —</option>
                {dids.map(d => (
                  <option key={d.id} value={d.id}>{d.number}</option>
                ))}
              </select>
              {didsError && <small style={{ color: '#c0392b' }}>Could not load available numbers from billing.</small>}
              {!didsError && dids.length === 0 && (
                <small style={{ color: '#c0392b' }}>No free numbers available. Contact support.</small>
              )}
            </div>
          )}
        </div>

        {/* Template */}
        <div className="form-section">
          <h3>Template</h3>
          <div className="templates-grid">
            {TEMPLATES.filter(t => OUTBOUND_ENABLED || t.id !== 'outbound_sales').map(t => (
              <div
                key={t.id}
                className={`template-card ${form.template === t.id ? 'template-card--active' : ''}`}
                onClick={() => applyTemplate(t.id)}
              >
                <span className="template-icon">{t.icon}</span>
                <strong>{t.name}</strong>
                <small>{t.desc}</small>
              </div>
            ))}
          </div>
        </div>

        {/* Basic Info */}
        <div className="form-section">
          <h3>Basic Settings</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Agent Name *</label>
              <input className="input" type="text" placeholder="e.g. Sophie - Restaurant Receptionist"
                value={form.name} onChange={e => update('name', e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Timezone</label>
              <select className="input" value={form.timezone} onChange={e => update('timezone', e.target.value)}>
                {TIMEZONE_OPTIONS.map(timezone => (
                  <option key={timezone} value={timezone}>{timezone}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Language</label>
              <select className="input" value={form.language} onChange={e => update('language', e.target.value)}>
                {LANGUAGE_OPTIONS.map(language => (
                  <option key={language.value} value={language.value}>{language.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Voice</label>
              <select className="input" value={form.voice} onChange={e => update('voice', e.target.value)}>
                {voices.length > 0 ? voices.map(v => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                )) : (
                  <option value="" disabled>Loading voices…</option>
                )}
              </select>
            </div>
          </div>
        </div>

        {/* Welcome Message */}
        <div className="form-section">
          <h3>Welcome Message</h3>
          <div className="toggle-row">
            <label>Enable welcome message</label>
            <input type="checkbox" checked={form.welcome_message_enabled}
              onChange={e => update('welcome_message_enabled', e.target.checked)} />
          </div>
          {form.welcome_message_enabled && (
            <>
              <div className="form-group">
                <label>Welcome Message Text</label>
                <textarea className="input textarea" rows={3}
                  placeholder="Hello! Thank you for calling. How can I help you today?"
                  value={form.welcome_message_text}
                  onChange={e => update('welcome_message_text', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Delay (seconds): {form.welcome_message_delay}</label>
                <input type="range" min={0} max={3} step={0.5} value={form.welcome_message_delay}
                  onChange={e => update('welcome_message_delay', parseFloat(e.target.value))} />
              </div>
            </>
          )}
        </div>

        {/* System Prompt */}
        <div className="form-section">
          <h3>Agent Script / Instructions</h3>
          <div className="form-group">
            <label>System Instructions</label>
            <textarea className="input textarea" rows={5}
              placeholder="Describe how your agent should behave, what information to collect, how to respond to common questions..."
              value={form.system_prompt}
              onChange={e => update('system_prompt', e.target.value)} />
            <small>These instructions define your agent's behavior and personality.</small>
          </div>
        </div>

        {OUTBOUND_ENABLED && (
          <div className="form-section">
            {/* Voicemail */}
            <h3>Voicemail Detection</h3>
            <div className="toggle-row">
              <label>Enable voicemail detection</label>
              <input type="checkbox" checked={form.voicemail_detection}
                onChange={e => update('voicemail_detection', e.target.checked)} />
            </div>
            {form.voicemail_detection && (
              <div className="form-group">
                <label>Voicemail message</label>
                <textarea className="input textarea" rows={2}
                  value={form.voicemail_message}
                  onChange={e => update('voicemail_message', e.target.value)} />
              </div>
            )}
          </div>
        )}

        {/* Recording */}
        <div className="form-section">
          <div className="toggle-row">
            <div>
              <h3 style={{ margin: 0 }}>Call Recording</h3>
              <small>Record all calls for review</small>
            </div>
            <input type="checkbox" checked={form.recording_enabled}
              onChange={e => update('recording_enabled', e.target.checked)} />
          </div>
        </div>

        {/* Submit */}
        <div className="form-actions">
          <button type="button" className="btn btn-ghost" onClick={() => navigate('/dashboard')}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            <Save size={16} />
            {loading ? 'Creating...' : 'Create Agent'}
          </button>
        </div>
      </form>
    </div>
  );
}
